/**
 * Geocoding utility using Nominatim (OpenStreetMap)
 */

export interface GeocodeResult {
  lat: number;
  lng: number;
  displayName: string;
}

export type GeocodeErrorCode = 'RATE_LIMIT' | 'NOT_FOUND' | 'NETWORK_ERROR' | 'UNKNOWN';

export interface GeocodeError {
  code: GeocodeErrorCode;
  statusCode?: number;
  message: string;
  retryAfter?: number; // Seconds to wait before retrying (from Retry-After header)
}

export interface GeocodeResponse {
  success: boolean;
  result?: GeocodeResult;
  error?: GeocodeError;
}

interface CachedGeocode {
  address: string;
  result: GeocodeResult;
  timestamp: number;
}

const CACHE_KEY = 'delivery-geocoding-cache';
const ADDRESS_CACHE_KEY = 'delivery-address-geocoding-cache';
const CACHE_TTL_DAYS = 30;

/**
 * Normalizes an address for cache lookups (case-insensitive, whitespace-normalized)
 * @param address - Raw address string
 * @returns Normalized address string for cache key
 */
const normalizeAddress = (address: string): string => {
  return address.toLowerCase().trim().replace(/\s+/g, ' ');
};

/**
 * Converts an address to lat/lng coordinates using Nominatim geocoding service
 * Checks cache first to avoid unnecessary API calls
 * @param address - Full address string
 * @returns Promise with structured response containing result or error details
 */
export const geocodeAddress = async (address: string): Promise<GeocodeResponse> => {
  // Check cache first
  const cachedResult = getCachedGeocodeByAddress(address);
  if (cachedResult) {
    console.log('✓ Cache hit for address:', address, '→', cachedResult);
    return { success: true, result: cachedResult };
  }

  try {
    // Format the address for the API
    const encodedAddress = encodeURIComponent(address);

    // Use Nominatim API (OpenStreetMap's geocoding service)
    console.log('→ Making Nominatim API call for:', address);
    const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`, {
      headers: {
        'User-Agent': 'RecipeManagementApp/1.0' // Required by Nominatim
      }
    });

    if (!response.ok) {
      // Parse HTTP status codes to determine error type
      let errorCode: GeocodeErrorCode;
      let message: string;
      let retryAfter: number | undefined;

      if (response.status === 503) {
        errorCode = 'RATE_LIMIT';
        message = 'Geocoding service is rate-limited or temporarily unavailable';
        // Try to extract Retry-After header if present
        const retryAfterHeader = response.headers.get('Retry-After');
        if (retryAfterHeader) {
          retryAfter = parseInt(retryAfterHeader, 10);
        }
        console.error(`[Geocoding] ⚠️ 503 Rate Limit: ${message}${retryAfter ? ` (retry after ${retryAfter}s)` : ''}`);
      } else if (response.status === 429) {
        errorCode = 'RATE_LIMIT';
        message = 'Too many requests - rate limit exceeded';
        console.error(`[Geocoding] ⚠️ 429 Rate Limit: ${message}`);
      } else {
        errorCode = 'NETWORK_ERROR';
        message = `HTTP ${response.status}: ${response.statusText}`;
        console.error(`[Geocoding] ❌ Network Error: ${message}`);
      }

      return {
        success: false,
        error: {
          code: errorCode,
          statusCode: response.status,
          message,
          retryAfter
        }
      };
    }

    const data = await response.json();

    if (!data || data.length === 0) {
      console.warn('[Geocoding] ⚠️ No results found for address:', address);
      return {
        success: false,
        error: {
          code: 'NOT_FOUND',
          statusCode: 404,
          message: `No geocoding results found for address: ${address}`
        }
      };
    }

    const result = data[0];
    const geocodeResult = {
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      displayName: result.display_name
    };

    console.log('[Geocoding] ✓ Success:', address, '→', geocodeResult);

    // Cache the result
    setCachedGeocodeByAddress(address, geocodeResult);

    return { success: true, result: geocodeResult };
  } catch (error) {
    console.error('[Geocoding] ❌ Exception:', address, error);
    return {
      success: false,
      error: {
        code: 'UNKNOWN',
        message: error instanceof Error ? error.message : 'Unknown geocoding error'
      }
    };
  }
};

/**
 * Formats an order address into a single string for geocoding
 * @param streetAddress - Street address
 * @param addressLine2 - Optional address line 2
 * @param city - City
 * @param province - Province/State
 * @param postalCode - Postal code
 * @returns Formatted address string
 */
export const formatOrderAddress = (
  streetAddress: string,
  addressLine2: string,
  city: string,
  province: string,
  postalCode: string
): string => {
  const parts = [streetAddress, addressLine2, city, province, postalCode].filter(part => part && part.trim() !== '');

  return parts.join(', ');
};

/**
 * Geocodes an address with fallback formats if the primary format fails
 * Tries multiple address variations to maximize success rate
 * @param streetAddress - Street address
 * @param addressLine2 - Optional address line 2
 * @param city - City
 * @param province - Province/State
 * @param postalCode - Postal code
 * @returns Promise with geocode response (success or error)
 */
export const geocodeAddressWithFallback = async (
  streetAddress: string,
  addressLine2: string,
  city: string,
  province: string,
  postalCode: string
): Promise<GeocodeResponse> => {
  // Try formats in order of specificity
  const formats = [
    // Format 1: Full address
    formatOrderAddress(streetAddress, addressLine2, city, province, postalCode),
    // Format 2: Without addressLine2
    formatOrderAddress(streetAddress, '', city, province, postalCode),
    // Format 3: Without postal code
    formatOrderAddress(streetAddress, addressLine2, city, province, ''),
    // Format 4: City + Province + Canada
    `${city}, ${province}, Canada`,
    // Format 5: City + Full province name + Canada
    `${city}, Manitoba, Canada`
  ];

  // Remove duplicates
  const uniqueFormats = Array.from(new Set(formats));

  let lastError: GeocodeError | undefined;

  for (let i = 0; i < uniqueFormats.length; i++) {
    const format = uniqueFormats[i];
    console.log(`[Geocoding] Trying format ${i + 1}/${uniqueFormats.length}:`, format);

    const response = await geocodeAddress(format);

    if (response.success) {
      if (i > 0) {
        console.warn(`[Geocoding] ⚠️ Used fallback format ${i + 1}:`, format);
      }
      return response;
    }

    // Store the error
    lastError = response.error;

    // If we got a rate limit error, stop trying other formats
    // (no point trying different address formats if we're being rate limited)
    if (response.error?.code === 'RATE_LIMIT') {
      console.warn('[Geocoding] ⚠️ Rate limit detected - stopping fallback attempts');
      return response; // Return the rate limit error immediately
    }

    // For NOT_FOUND errors, try the next format
    // Wait between attempts to respect rate limits (1100ms > Nominatim's 1 req/sec)
    if (i < uniqueFormats.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 1100));
    }
  }

  // All formats failed - return the last error
  return {
    success: false,
    error: lastError || {
      code: 'UNKNOWN',
      message: 'All geocoding formats failed'
    }
  };
};

/**
 * Retrieves a cached geocode result for an order
 * @param orderId - Order ID to look up
 * @returns Cached geocode result or null if not found/expired
 */
export const getCachedGeocode = (orderId: string): GeocodeResult | null => {
  try {
    const cache = localStorage.getItem(CACHE_KEY);
    if (!cache) return null;

    const parsed: Record<string, CachedGeocode> = JSON.parse(cache);
    const entry = parsed[orderId];

    if (!entry) return null;

    // Check if expired
    const age = Date.now() - entry.timestamp;
    const maxAge = CACHE_TTL_DAYS * 24 * 60 * 60 * 1000;

    if (age > maxAge) {
      return null;
    }

    return entry.result;
  } catch (error) {
    console.error('Failed to read geocoding cache:', error);
    return null;
  }
};

/**
 * Saves a geocode result to cache
 * @param orderId - Order ID to cache under
 * @param address - The address that was geocoded
 * @param result - The geocode result to cache
 */
export const setCachedGeocode = (orderId: string, address: string, result: GeocodeResult): void => {
  try {
    const cache = localStorage.getItem(CACHE_KEY);
    const parsed: Record<string, CachedGeocode> = cache ? JSON.parse(cache) : {};

    parsed[orderId] = {
      address,
      result,
      timestamp: Date.now()
    };

    localStorage.setItem(CACHE_KEY, JSON.stringify(parsed));
  } catch (error) {
    console.error('Failed to save geocoding cache:', error);
  }
};

/**
 * Clears all cached geocode results
 */
export const clearGeocodingCache = (): void => {
  localStorage.removeItem(CACHE_KEY);
  localStorage.removeItem(ADDRESS_CACHE_KEY);
};

/**
 * Retrieves a cached geocode result by address (case-insensitive)
 * @param address - Address to look up
 * @returns Cached geocode result or null if not found/expired
 */
export const getCachedGeocodeByAddress = (address: string): GeocodeResult | null => {
  try {
    const cache = localStorage.getItem(ADDRESS_CACHE_KEY);
    if (!cache) return null;

    const normalizedKey = normalizeAddress(address);
    const parsed: Record<string, CachedGeocode> = JSON.parse(cache);
    const entry = parsed[normalizedKey];

    if (!entry) return null;

    // Check if expired
    const age = Date.now() - entry.timestamp;
    const maxAge = CACHE_TTL_DAYS * 24 * 60 * 60 * 1000;

    if (age > maxAge) {
      return null;
    }

    return entry.result;
  } catch (error) {
    console.error('Failed to read address geocoding cache:', error);
    return null;
  }
};

/**
 * Saves a geocode result to cache by address
 * @param address - The address that was geocoded
 * @param result - The geocode result to cache
 */
export const setCachedGeocodeByAddress = (address: string, result: GeocodeResult): void => {
  try {
    const cache = localStorage.getItem(ADDRESS_CACHE_KEY);
    const parsed: Record<string, CachedGeocode> = cache ? JSON.parse(cache) : {};

    const normalizedKey = normalizeAddress(address);
    parsed[normalizedKey] = {
      address,
      result,
      timestamp: Date.now()
    };

    localStorage.setItem(ADDRESS_CACHE_KEY, JSON.stringify(parsed));
    console.log('✓ Cached geocode result for:', address);
  } catch (error) {
    console.error('Failed to save address geocoding cache:', error);
  }
};
